import React from 'react';
import { ShieldCheck, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import { useLoading } from '../hooks/useLoading';
import { sleep } from '../utils/sleep';

const Compliance = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRunCheck = async () => {
    await withLoading(async () => {
      // Simulate API call
      await sleep(3000);
      console.log('Running compliance check...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Running compliance check..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ShieldCheck className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Compliance & Audit</h1>
            <p className="text-sm text-gray-400">Monitor compliance status and audit trails</p>
          </div>
        </div>
        <Button onClick={handleRunCheck}>Run Compliance Check</Button>
      </div>

      {/* Compliance Status */}
      <div className="grid grid-cols-4 gap-6">
        {['SOC 2', 'ISO 27001', 'GDPR', 'HIPAA'].map((standard) => (
          <div key={standard} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{standard}</h3>
              <div className="flex items-center space-x-2 text-emerald-500">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm">Compliant</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Controls</span>
                <span className="text-white">45/50</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: '90%' }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Compliance Issues */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Compliance Issues</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center space-x-4">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <div>
                  <div className="text-sm font-medium text-white">Password Policy Non-Compliance</div>
                  <div className="text-xs text-gray-400 mt-1">
                    Password requirements do not meet minimum complexity standards
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-xs text-yellow-500">Medium Risk</span>
                <Button variant="outline" size="sm">View Details</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Audit Log */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Audit Log</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
              <div>
                <div className="text-sm font-medium text-white">Security Policy Updated</div>
                <div className="text-xs text-gray-400 mt-1">
                  By John Smith • 2 hours ago
                </div>
              </div>
              <Button variant="outline" size="sm">View Changes</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Compliance;